import { TimespanDisplayRecord } from "../../types/immutableRecords/display/shared/TimespanDisplayRecord";
import { TimespanRecord } from "../../types/immutableRecords/model/data/TimespanRecord";
import dateFunctions, { isDateValid } from "../definition/calendar/dateFunctions";
import dateByAddingSpan from "../definition/calendar/dateFunctions/dateByAddingSpan";
import dateSubtractionFunctions from "../definition/calendar/dateFunctions/dateSubtractionFunctions";
import { stringFromDate, stringFromEndDate } from "../definition/calendar/dateFunctions/stringFromDate";
import {
  isTimespanNegative,
  isTimespanZero,
  stringFromTimespan,
  stringFromTimespanRange
} from "../definition/calendar/dateFunctions/timespanFunctions";

const isAncestorOfItem = (itemsById, ancestorId, targetId) => {
  let newParent = itemsById.get(targetId);
  while (newParent.super !== null) {
    if (ancestorId === newParent.super || ancestorId === newParent.id) {
      return true;
    }
    newParent = itemsById.get(newParent.super);
  }
  return false;
};

/**
 * @param {DateRecord} start
 * @param {DateRecord} latestStart
 * @param {CalendarRecord} calendar
 * @return {TimespanRecord|null}
 */
const getOngoingDuration = (start, latestStart, calendar, todayDate) => {
  let longDuration = null;
  let shortDuration = null;
  const dateForNow = todayDate ? todayDate : dateFunctions.dateForNow(calendar);
  if (isDateValid(start)) {
    longDuration = dateSubtractionFunctions.timespanBetweenDates(calendar, start, dateForNow);
    if (isTimespanNegative(longDuration)) {
      longDuration = null;
    }
  }
  if (isDateValid(latestStart)) {
    shortDuration = dateSubtractionFunctions.timespanBetweenDates(calendar, latestStart, dateForNow);
    if (isTimespanNegative(shortDuration)) {
      shortDuration = null;
    }
  }
  return longDuration
    ? TimespanDisplayRecord(longDuration).set(
      "label",
      shortDuration ? stringFromTimespanRange(shortDuration, longDuration, 2) : stringFromTimespan(longDuration, 2)
    )
    : shortDuration
      ? TimespanDisplayRecord(shortDuration).set("label", stringFromTimespan(shortDuration, 2))
      : null;
};

/**
 *
 * @param {Immutable.Map|ItemRecord} obj
 * @param {Calendar} calendar
 */
const getDurationRangeLabel = (obj, calendar) => {
  const start = obj.get("preferredStartDate");
  const latestStart = obj.get("latestStartDate");
  const durationToEarliestEnd = obj.get("durationToEarliestEnd");
  const preferredDuration = obj.get("preferredDuration");

  return getDurationRangeLabelForDates(start, latestStart, preferredDuration, durationToEarliestEnd, calendar);
};

/**
 *
 * @param {DateRecord} start
 * @param {DateRecord} latestStart
 * @param {TimespanRecord} preferredDuration
 * @param {TimespanRecord} durationToEarliestEnd
 * @param {CalendarRecord} calendar
 */
const getDurationRangeLabelForDates = (start, latestStart, preferredDuration, durationToEarliestEnd, calendar) => {
  let shortDuration = preferredDuration;
  let longDuration = preferredDuration;

  if (isDateValid(latestStart)) {
    if (!durationToEarliestEnd) {
      // Special case that when duration is not set but
      // the latest start is existed
      // The end date should following the latest date.
      if (isTimespanZero(preferredDuration)) {
        shortDuration = TimespanRecord();
        longDuration = dateSubtractionFunctions.timespanBetweenDates(calendar, start, latestStart);
      } else {
        shortDuration = dateSubtractionFunctions.timespanBetweenDates(
          calendar,
          latestStart,
          dateByAddingSpan(calendar, start, preferredDuration)
        );
      }
    } else {
      shortDuration = isDateValid(start)
        ? dateSubtractionFunctions.timespanBetweenDates(
          calendar,
          latestStart,
          dateByAddingSpan(calendar, start, durationToEarliestEnd)
        )
        : durationToEarliestEnd;
    }
  } else {
    if (!durationToEarliestEnd) {
      shortDuration = preferredDuration;
    } else {
      shortDuration = durationToEarliestEnd;
    }
  }

  const componentLimit = durationToEarliestEnd || isDateValid(latestStart) ? 2 : null;

  return stringFromTimespanRange(shortDuration, longDuration, componentLimit);
};

const arePreferredStartDifferentFromActualStart = item =>
  !dateFunctions.areDatesSame(item.preferredStartDate, item.startDate);

const arePreferredEndDifferentFromActualEnd = item => !dateFunctions.areDatesSame(item.preferredEndDate, item.endDate);

const arePreferredDatesDifferentFromActualDates = item =>
  arePreferredStartDifferentFromActualStart(item) || arePreferredEndDifferentFromActualEnd(item);

const getActualDateStrings = (calendar, item) => {
  const { startDate, hasOngoingDescendants, endDate, duration, ongoing } = item;
  const dateForNow = dateFunctions.dateForNow(calendar);
  const startStr = `${stringFromDate(calendar, startDate)}`;
  const endStr =
    (hasOngoingDescendants || ongoing) && endDate.get("timestamp") < dateForNow.get("timestamp")
      ? "Present"
      : `${stringFromEndDate(calendar, endDate, startDate)}`;
  const durationStr = stringFromTimespan(duration, 2);
  return { startStr, endStr, durationStr };
};

export default {
  isAncestorOfItem,
  getOngoingDuration,
  getDurationRangeLabel,
  getDurationRangeLabelForDates,
  arePreferredStartDifferentFromActualStart,
  arePreferredEndDifferentFromActualEnd,
  arePreferredDatesDifferentFromActualDates,
  getActualDateStrings
};
